'use client'

import { BotMessageSquare } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import AccountDropdown from '@/components/account-dropdown'
import LogoText from '@/components/icons/logo-text.svg'
import Logo from '@/components/icons/logo.svg'
import NotificationDropdown from '@/components/notification-dropdown'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useReadMineUserQueryWhenLogin from '@/hooks/use-read-mine-user-query-when-login'
import { cn } from '@/lib/utils'

import activeNavigationLg from './active-navigation-lg.png'
import activeNavigationSm from './active-navigation-sm.png'
import activeNavigation from './active-navigation.png'

const navigations = [{
  title: 'Home',
  href: '/',
  activeImage: activeNavigation,
}, {
  title: 'Event',
  href: '/competition',
  activeImage: activeNavigation,
  comingSoon: false,
}, {
  title: 'Leaderboard',
  href: '/leaderboard',
  activeImage: activeNavigationLg,
}, {
  title: 'Bots',
  href: '/bots',
  activeImage: activeNavigationSm,
}, {
  title: 'About',
  href: '/about',
  activeImage: activeNavigation,
}, {
  title: 'Docs',
  href: 'https://docs.axgrad.ai/',
  activeImage: activeNavigationSm,
}]

export default function Header() {
  const pathname = usePathname()
  const isHome = pathname === '/'
  const { data: mineUser } = useReadMineUserQueryWhenLogin()

  return (
    <div className={cn(!isHome && 'pt-17')}>
      <div
        className={cn(
          'fixed z-4 flex items-center justify-between pl-10',
          isHome
            ? 'w-(--content-width) bg-[url("/home-images/navigation-background.png")] rounded-full h-21 top-8 left-1/2 -translate-x-1/2 bg-contain bg-no-repeat box-border pr-6 backdrop-blur-[18.65px] shadow-[-20px_68px_20px_0_rgba(0,0,0,0),_-13px_43px_18px_0_rgba(0,0,0,0.01),_-7px_24px_15px_0_rgba(0,0,0,0.04),_-3px_11px_11px_0_rgba(0,0,0,0.07),_-1px_3px_6px_0_rgba(0,0,0,0.08)]'
            : 'w-full h-17 pr-10 top-0 bg-background',
        )}
      >
        <Link href="/" className="flex items-center gap-2">
          <Logo className="h-9" />
          <LogoText className="h-6" />
        </Link>

        <div className="flex gap-2">
          {navigations.map(({ title, href, comingSoon, activeImage }) => {
            const isActive = href === '/' ? isHome : pathname.startsWith(href)
            return comingSoon ? (
              <TooltipProvider key={title}>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="h-8 px-4 rounded-2xl flex items-center text-disabled-foreground cursor-not-allowed">
                      {title}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent withArrow>
                    <p>Coming Soon</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Link
                key={title}
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                className="h-8 px-4 rounded-2xl flex items-center hover:bg-border-button-hover bg-contain bg-no-repeat"
                style={isActive ? { backgroundImage: `url(${activeImage.src})` } : undefined}
              >
                {title}
              </Link>
            )
          })}
        </div>

        <div className="flex gap-6 items-center">
          {mineUser && (
            <Link href="/bot-management" className="text-sm font-semibold flex items-center gap-1">
              <BotMessageSquare className="size-4" />
              AI Co-Pilot
            </Link>
          )}
          <NotificationDropdown />
          <AccountDropdown isHome={isHome} />
        </div>
      </div>
    </div>
  )
}
